import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APPCONSTANTS } from 'src/app/app.constants';
import { environment } from 'src/environments/environment';
import { AdminApiService } from './admin-api.service';
import { AmplifyService } from './amplify.service';
import { AssetService } from './asset.service';
import { CcmcApiService } from './ccmc-api.service';
import { CustomErrorHandlerService } from './custom-error-handler.service';
import { CCMCSelectedFieldService } from './selected-field.service';
import * as AWS from 'aws-sdk';
const { Buffer } = require('buffer');
@Injectable({
  providedIn: 'root'
})
export class DocumentConnectorService {
  private ASSET_ID_LOAN_NUMBER_INDEX = "assetID-loanNumber-index";
  private ASSET_ID_DATE_INDEX = "assetID-date-index";
  private DOC_CONNECTOR_TABLE_NAME = "encompass_document_connector_history";
  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };
  baseUrl: any;
  documentBackup: any;
  private _dcDocument: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public sendButtonFlag: BehaviorSubject<any> = new BehaviorSubject<any>(false);

  /**
   * Emits the loan that was most recently submitted for processing, with the time
   * it happened.
   *
   * The backend accepts the request and works in the background, so the loan's
   * status does not change until it writes its first update. The history view
   * uses this to poll aggressively for a short period after a send, so the loan
   * visibly moves to Queued instead of appearing unchanged.
   *
   * The timestamp matters because this replays its last value to any new
   * subscriber, and the history view is recreated on navigation. Without it, an
   * old submission would look current every time the page is opened.
   */
  public loanSubmitted: BehaviorSubject<{ loanGUID: string; at: number } | undefined> =
    new BehaviorSubject<{ loanGUID: string; at: number } | undefined>(undefined);

  /**
   * Announces that a loan was handed to the backend for processing.
   *
   * @param loanGUID Loan that was submitted.
   */
  notifyLoanSubmitted(loanGUID: string) {
    this.loanSubmitted.next({ loanGUID, at: Date.now() });
  }
  constructor(
    private assetService: AssetService,
    private http: HttpClient,
    private customErrorHandlerService: CustomErrorHandlerService,
    private selectedFieldService: CCMCSelectedFieldService,
    private ccmcApiService: CcmcApiService,
    private adminApiService: AdminApiService,
    private amplifyService: AmplifyService
  ) {
    this.httpOptions.headers.append('Access-Control-Allow-Origin', '*');
    this.httpOptions.headers.append(
      'Access-Control-Allow-Methods',
      'POST, GET, OPTIONS, PUT'
    );
    this.httpOptions.headers.append('Accept', 'application/json');
  }

  get dcDocument() {
    return this._dcDocument.asObservable().pipe(
      map(document => {
        return document;
      })
    );
  }

  setDocumentConnectorDocument(dcDocument: any) {
    if (dcDocument.globalMetas) {
      this.setGlobalMetasEditedFlags(dcDocument.globalMetas);
    }
    if (dcDocument.destinations) {
      this.setDestinationsEditedFlags(dcDocument.destinations);
    }
    this.documentBackup = JSON.parse(JSON.stringify(dcDocument));
    this._dcDocument.next(dcDocument);
  }

  setDcDocumentSimple(dcDocument: any) {
    if (dcDocument.globalMetas) {
      this.selectedFieldService.onGlobalMetasFieldSelected.next(
        dcDocument.globalMetas[0]
      );
    }
    this._dcDocument.next(dcDocument);
  }

  setGlobalMetasEditedFlags(globalMetas: any) {
    for (let globalMetaItem of globalMetas) {
      globalMetaItem.edited = false;
    }
  }

  setDestinationsEditedFlags(destinations: any) {
    for (let destination of destinations) {
      destination.edited = false;
    }
  }

  ice(loan: any) {
    const assetID = this.assetService.getSelectedAssetId();
    const body = JSON.stringify({
      assetID: assetID,
      content: {
        data: loan
      }
    });
    return this.http
      .post(`${Buffer.from(environment.environmentURL, "base64").toString()}/ice/data-conversion-gla`, body, this.httpOptions)
      .pipe(catchError(this.customErrorHandlerService.handleError));
  }



  async getDocConnectorHistory(assetID: any) {
    console.log('assetID', assetID);
    return new Promise(resolve => {
      this.ccmcApiService
        .getDynamoDBItemsByIndex(
          this.DOC_CONNECTOR_TABLE_NAME,
          assetID,
          'assetID',
          this.ASSET_ID_DATE_INDEX
        )
        .subscribe(dcHistoryDocument => {
          console.log(dcHistoryDocument);
          const parsedDCDocumentResult = JSON.parse(
            JSON.stringify(dcHistoryDocument)
          );
          return resolve(parsedDCDocumentResult);
        });
    });
  }

  /**
   * Queries doc connector history for an asset, newest first.
   *
   * @param assetID Asset whose history is being read.
   * @param lastEvaluatedKey Pagination token, or undefined to start at the newest record.
   * @param limit Optional cap on items returned. Supply this for polling so a
   *              background refresh reads a bounded page instead of the whole
   *              asset partition.
   */
  async getMoreDocConnectorHistory(assetID: any, lastEvaluatedKey: any, limit?: number) {
    console.log("GETTING MORE HISTORY WITH GSI")
    return new Promise(resolve => {
      // Use direct HTTP call with proper GSI parameters
      const body: any = {
        tableName: this.DOC_CONNECTOR_TABLE_NAME,
        indexName: this.ASSET_ID_DATE_INDEX,
        primaryKeyAttribute: 'assetID',
        primaryKey: assetID,
        lastEvaluatedKey: lastEvaluatedKey,
        scanIndexForward: false // Descending order (newest first)
      };

      if (limit) {
        body.limit = limit;
      }

      this.http.post(
        `${Buffer.from(environment.environmentURL, "base64").toString()}/dynamodb/get-documents-by-index`,
        body,
        this.httpOptions
      ).pipe(
        catchError(this.customErrorHandlerService.handleError)
      ).subscribe(response => {
        const parsedResult = JSON.parse(JSON.stringify(response));
        return resolve(parsedResult);
      });
    });
  }

  async searchDocConnectorHistory(assetID: any, searchTerm: string, limit: number, lastEvaluatedKey?: any) {
    console.log("SEARCHING FOR SPECIFIC RECORD:", searchTerm);
    return new Promise(resolve => {
      // Check if searchTerm looks like a GUID (contains hyphens and is 36 chars)
      const isGUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchTerm);
      
      const body: any = {
        tableName: this.DOC_CONNECTOR_TABLE_NAME,
        indexName: isGUID ? "" : this.ASSET_ID_LOAN_NUMBER_INDEX, 
        assetID: assetID,
        searchTerm: searchTerm,
        searchField: isGUID ? 'loanGUID' : 'loanNumber',
        limit
      };

      // Include lastEvaluatedKey in request body if provided for pagination
      if (lastEvaluatedKey) {
        body.lastEvaluatedKey = lastEvaluatedKey;
      }

      console.log(`Searching by ${body.searchField}:`, searchTerm);

      this.http.post(
        `${Buffer.from(environment.environmentURL, "base64").toString()}/dynamodb/search`,
        body,
        this.httpOptions
      ).pipe(
        catchError(error => {
          console.error('Search API error:', error);
          return [{
            statusFlag: false,
            statusMessage: 'Search failed',
            content: { Items: [] }
          }];
        })
      ).subscribe(response => {
        const parsedResult = JSON.parse(JSON.stringify(response));
        return resolve(parsedResult);
      });
    });
  }

  createDocument(losConnectionData: any) {
    console.log('los Connection Data', losConnectionData);
    const assetID = this.assetService.getSelectedAssetId();
    const currentDoc = this._dcDocument.getValue();
    this.documentBackup = JSON.parse(JSON.stringify(currentDoc));
    let tempDocumentArray: any[] = [];
    currentDoc.destinations.forEach(function (item: any) {
      const tempItem = Object.assign({}, item);
      delete tempItem.edited;
      tempDocumentArray.push(tempItem);
    });
    currentDoc.destinations = tempDocumentArray;
    tempDocumentArray = [];
    currentDoc.globalMetas.forEach(function (item: any) {
      const tempItem = Object.assign({}, item);
      delete tempItem.edited;
      tempDocumentArray.push(tempItem);
    });
    currentDoc.globalMetas = tempDocumentArray;
    console.log('Current Doc', currentDoc);
    const body = {
      cluster: losConnectionData.couchbaseConnectionString,
      assetID: assetID,
      bucket: losConnectionData.bucket,
      document: { ...currentDoc }
    };
    console.log(body);
    return this.http
      .post(
        `${Buffer.from(environment.environmentURL, "base64").toString()}/couchbase/create-document`,
        body,
        this.httpOptions
      )
      .pipe(catchError(this.customErrorHandlerService.handleError));
  }

  async sendToICE(docConnectorRequest: any) {
    return ( this.http
      .post(`${Buffer.from(environment.environmentURL, "base64").toString()}/ice/doc-connector`, docConnectorRequest, this.httpOptions)
      .pipe(catchError(this.customErrorHandlerService.handleError))
    );
  }

  async sendToDocListener(docConnectorRequest: any) {
    console.log(docConnectorRequest);
      return  ( this.http
      .post(`${Buffer.from(environment.environmentURL, "base64").toString()}/encompass/doc-connector-listener`, docConnectorRequest, this.httpOptions)
      .pipe(catchError(this.customErrorHandlerService.handleError))
      );
    
  }

  trailingDates(trailingDate: any) {
    // Formats date to M/DD/YYYY, H:MM:MS AM / PM
    if (trailingDate) {
      const displayDate = new Date(trailingDate);
      return displayDate.toLocaleString();
    }

    return;
  }
}
